import { TTickEvent } from './EventLog';

/**
 * Event recording & export subsystem (Step 6).
 * Captures ticks, markers, and user actions with optional compression.
 * Provides retention, export, and import/decompression helpers for QA.
 */

// ----------------------------- Event Schema -----------------------------

export type TReplayEventType = 'tick' | 'marker' | 'action';
export type TUserActionType =
    | 'rewind'
    | 'play'
    | 'pause'
    | 'stop'
    | 'step_forward'
    | 'step_back'
    | 'speed_change'
    | 'jump_live';

export interface TBaseReplayEvent {
    id: number;            // monotonically increasing id (local)
    t: TReplayEventType;   // event category
    e: number;             // epoch (ms)
}

export interface TTickReplayEvent extends TBaseReplayEvent {
    t: 'tick';
    p: number;             // price (close)
    v?: number;            // volume (if available)
    o?: number;            // open (candle)
    h?: number;            // high (candle)
    l?: number;            // low (candle)
    s?: string;            // symbol (optional enrichment)
    g?: number;            // granularity ms (0 for tick)
}

export interface TMarkerReplayEvent extends TBaseReplayEvent {
    t: 'marker';
    k: string;             // marker kind (e.g., 'trade', 'indicator', etc.)
    d?: Record<string, unknown>; // marker data payload
}

export interface TActionReplayEvent extends TBaseReplayEvent {
    t: 'action';
    a: TUserActionType;    // action type
    d?: Record<string, unknown>; // action metadata (speed, targetEpoch, etc.)
}

export type TReplayEvent = TTickReplayEvent | TMarkerReplayEvent | TActionReplayEvent;

// ----------------------------- Retention Policy -----------------------------

interface TRecorderConfig {
    maxEvents: number;          // hard cap of recorded events
    trimChunk: number;          // amount to trim once exceeding cap
    enableCompression: boolean; // toggle compression on export
}

const DEFAULT_CONFIG: TRecorderConfig = {
    maxEvents: 200_000,
    trimChunk: 20_000,
    enableCompression: true,
};

// ----------------------------- Compression Format -----------------------------

/**
 * Compression strategy:
 * - Delta encode tick prices relative to previous tick (store first absolute then deltas)
 * - Run-length encode consecutive identical volumes
 * - Actions & markers kept verbatim (frequency low)
 */

interface TCompressedExport {
    version: 1;
    symbol?: string;
    startEpoch: number | null;
    endEpoch: number | null;
    counts: { total: number; ticks: number; markers: number; actions: number };
    ticks: {
        basePrice: number | null;      // first absolute price
        epochs: number[];              // epochs aligned with deltas
        deltas: number[];              // price deltas (price[i] = basePrice + Σ delta[0..i])
        volumes?: Array<number | [number, number]>; // number or [value, runLength]
        o?: (number | null)[];
        h?: (number | null)[];
        l?: (number | null)[];
        c?: (number | null)[]; // close duplication retained if candle context useful
    };
    markers: Array<Omit<TMarkerReplayEvent, 'id'>>;
    actions: Array<Omit<TActionReplayEvent, 'id'>>;
}

// ----------------------------- Recorder Implementation -----------------------------

export class EventRecorder {
    private _events: TReplayEvent[] = [];
    private _nextId = 1;
    private _config: TRecorderConfig;

    constructor(config?: Partial<TRecorderConfig>) {
        this._config = { ...DEFAULT_CONFIG, ...(config || {}) };
    }

    // Record a tick event from quote + enriched tick event
    recordTick(evt: TTickEvent) {
        const tick: TTickReplayEvent = {
            id: this._nextId++,
            t: 'tick',
            e: evt.epoch,
            p: evt.price,
            v: (evt.raw as any).Volume,
            o: evt.ohlc?.open,
            h: evt.ohlc?.high,
            l: evt.ohlc?.low,
            s: evt.symbol || (evt.raw as any).Symbol,
            g: evt.granularity,
        };
        this._append(tick);
    }

    // Record a user action
    recordAction(action: TUserActionType, data?: Record<string, unknown>) {
        const evt: TActionReplayEvent = {
            id: this._nextId++,
            t: 'action',
            e: Date.now(),
            a: action,
            d: data,
        };
        this._append(evt);
    }

    // Record a marker (placeholder for future integration)
    recordMarker(kind: string, data?: Record<string, unknown>) {
        const evt: TMarkerReplayEvent = {
            id: this._nextId++,
            t: 'marker',
            e: Date.now(),
            k: kind,
            d: data,
        };
        this._append(evt);
    }

    private _append(evt: TReplayEvent) {
        this._events.push(evt);
        this._enforceRetention();
    }

    private _enforceRetention() {
        const { maxEvents, trimChunk } = this._config;
        if (this._events.length > maxEvents) {
            const remove = Math.min(trimChunk, this._events.length - maxEvents);
            this._events.splice(0, remove);
        }
    }

    get size() {
        return this._events.length;
    }

    get all(): TReplayEvent[] {
        return [...this._events];
    }

    clear() {
        this._events = [];
        this._nextId = 1;
    }

    // ----------------------------- Export / Import -----------------------------

    exportCompressed(): string {
        const ticks: TTickReplayEvent[] = [];
        const markers: TMarkerReplayEvent[] = [];
        const actions: TActionReplayEvent[] = [];

        for (const e of this._events) {
            switch (e.t) {
                case 'tick': ticks.push(e); break;
                case 'marker': markers.push(e); break;
                case 'action': actions.push(e); break;
            }
        }

        const startEpoch = this._events.length ? this._events[0].e : null;
        const endEpoch = this._events.length ? this._events[this._events.length - 1].e : null;

        const compressedTicks = this._compressTicks(ticks);

        const payload: TCompressedExport = {
            version: 1,
            symbol: ticks[0]?.s,
            startEpoch,
            endEpoch,
            counts: { total: this._events.length, ticks: ticks.length, markers: markers.length, actions: actions.length },
            ticks: compressedTicks,
            markers: markers.map(m => ({ t: 'marker', e: m.e, k: m.k, d: m.d })),
            actions: actions.map(a => ({ t: 'action', e: a.e, a: a.a, d: a.d })),
        };

        return JSON.stringify(payload);
    }

    importCompressed(json: string) {
        const payload = JSON.parse(json) as TCompressedExport;
        if (payload.version !== 1) throw new Error('Unsupported replay export version');

        const ticks = this._decompressTicks(payload.ticks);
        const events: TReplayEvent[] = [];
        let idCounter = 1;

        for (let i = 0; i < ticks.length; i++) {
            events.push({ ...ticks[i], id: idCounter++ });
        }
        for (const m of payload.markers) {
            events.push({ ...m, id: idCounter++ } as TMarkerReplayEvent);
        }
        for (const a of payload.actions) {
            events.push({ ...a, id: idCounter++ } as TActionReplayEvent);
        }

        // Sort by epoch to restore chronological order
        events.sort((a, b) => a.e - b.e);

        this._events = events;
        this._nextId = idCounter;
    }

    // ----------------------------- Compression Helpers -----------------------------

    private _compressTicks(ticks: TTickReplayEvent[]): TCompressedExport['ticks'] {
        if (!ticks.length) {
            return { basePrice: null, epochs: [], deltas: [] };
        }
        const epochs: number[] = [];
        const deltas: number[] = [];
        const volumes: Array<number | [number, number]> = [];
        const open: (number | null)[] = [];
        const high: (number | null)[] = [];
        const low: (number | null)[] = [];
        const close: (number | null)[] = [];

        let basePrice = ticks[0].p;
        let prevPrice = basePrice;
        let prevVol = ticks[0].v;
        let volRun = 1;

        for (let i = 0; i < ticks.length; i++) {
            const tk = ticks[i];
            epochs.push(tk.e);
            const delta = tk.p - prevPrice;
            deltas.push(delta);
            prevPrice = tk.p;

            // RLE volumes
            if (tk.v === prevVol) {
                volRun++;
            } else {
                if (prevVol !== undefined) {
                    volumes.push(volRun > 1 ? [prevVol as number, volRun] : (prevVol as number));
                }
                prevVol = tk.v;
                volRun = 1;
            }

            open.push(tk.o ?? null);
            high.push(tk.h ?? null);
            low.push(tk.l ?? null);
            close.push(tk.p ?? null);
        }
        // Flush last volume run
        if (prevVol !== undefined) {
            volumes.push(volRun > 1 ? [prevVol as number, volRun] : (prevVol as number));
        }

        return { basePrice, epochs, deltas, volumes, o: open, h: high, l: low, c: close };
    }

    private _decompressTicks(data: TCompressedExport['ticks']): TTickReplayEvent[] {
        if (!data.basePrice || !data.epochs.length) return [];
        const result: TTickReplayEvent[] = [];
        let price = data.basePrice;
        let expandedVolumes: number[] = [];

        // Expand volumes
        if (data.volumes) {
            for (const v of data.volumes) {
                if (Array.isArray(v)) {
                    for (let i = 0; i < v[1]; i++) expandedVolumes.push(v[0]);
                } else {
                    expandedVolumes.push(v);
                }
            }
        }

        for (let i = 0; i < data.epochs.length; i++) {
            price += data.deltas[i];
            result.push({
                id: 0, // temporary; will be set by importCompressed
                t: 'tick',
                e: data.epochs[i],
                p: price,
                v: expandedVolumes[i],
                o: data.o ? data.o[i] ?? undefined : undefined,
                h: data.h ? data.h[i] ?? undefined : undefined,
                l: data.l ? data.l[i] ?? undefined : undefined,
                s: undefined,
                g: undefined,
            });
        }
        return result;
    }
}

export const eventRecorder = new EventRecorder();

declare global {
    interface Window { __eventRecorder?: EventRecorder }
}

if (typeof window !== 'undefined') {
    window.__eventRecorder = eventRecorder;
}
